import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';
import { apiPath, getApiDebugInfo } from '../config/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(apiPath('api/users/me/'));
      setUser(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'utilisateur:', error);
      logout();
    }
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (username, password) => {
    const url = apiPath('api/token/');
    const debug = getApiDebugInfo();

    console.debug('[Auth] Login attempt', { url, debug });
    try {
      const response = await axios.post(url, { username, password });
      const { access, refresh } = response.data;
      localStorage.setItem('token', access);
      localStorage.setItem('refresh', refresh);
      setToken(access);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access}`;
      await fetchUser();
      return { success: true };
    } catch (error) {
      const detail = {
        url,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        code: error.code,
        configUrl: error.config?.url,
      };
      console.error('[Auth] Login error', detail);

      if (error.response?.status === 404) {
        return {
          success: false,
          error: `API inaccessible (404). Vérifiez que le backend répond sur ${debug.healthUrl}`,
          debug: { ...debug, ...detail },
        };
      }
      if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        return {
          success: false,
          error: 'Impossible de joindre l\'API. Vérifiez l\'URL et CORS.',
          debug: { ...debug, ...detail },
        };
      }
      return {
        success: false,
        error: error.response?.data?.detail || error.response?.data?.message || 'Erreur de connexion',
        debug: { ...debug, ...detail },
      };
    }
  };

  const value = {
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
